import { Suspense, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isNil } from 'lodash';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import {
    addNewDynamicSegmentFilter,
    editDynamicSegmentFilter,
    selectDynamicSegmentFiltersData,
} from 'store/slices/dynamicSegment';
// Import Hooks
import useToaster from 'hooks/UseToaster';
// Import UI Components
import { BusyLoader } from '@geneui/components';
// Import Containers
import { UIHashComparisonContainer } from './UIHashContainers';
// Import Components
import DynamicSegmentForms from './DynamicSegmentForms';
// Import Constants
import { AlertTypes, l } from 'constants/common';
// Import Styles
import 'assets/scss/dynamicSegmentForms.scss';

const { error } = AlertTypes;

const DynamicSegmentFilterFormWrapper = ({ data, setReadOnlyFilterContainer, editModeCancelHandler, tabActiveId }) => {
    const { t } = useTranslation();
    const { showToaster } = useToaster();
    const dispatch = useAppDispatch();
    const dynamicSegmentFiltersData = useAppSelector(selectDynamicSegmentFiltersData);
    const [formData, setFormData] = useState(null);
    const [errorData, setErrorData] = useState('');
    const [errorValidationScenario, setErrorValidationScenario] = useState(null);
    const { elementType, parentDisplayName } = data;

    useEffect(() => {
        return () => setReadOnlyFilterContainer(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkIdExist = useCallback(() => {
        isNil(data.id)
            ? dispatch(addNewDynamicSegmentFilter(cloneDeep({ data, tab: dynamicSegmentFiltersData[tabActiveId] })))
            : dispatch(editDynamicSegmentFilter(cloneDeep({ data, tab: dynamicSegmentFiltersData[tabActiveId] })));
        editModeCancelHandler();
        setReadOnlyFilterContainer(false);
        setErrorData('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, dynamicSegmentFiltersData, editModeCancelHandler, tabActiveId]);

    const handleApply = useCallback(() => {
        data.setValue(formData);
        const filtersGroup = dynamicSegmentFiltersData[tabActiveId].filters[data.parentHashKey];
        if (filtersGroup) {
            const filteredFiltersGroup = filtersGroup.find((elem) => {
                const compData = { ...data };
                delete compData.id;
                return JSON.stringify(elem) === JSON.stringify(compData);
            });
            if (!filteredFiltersGroup) {
                checkIdExist();
            } else {
                showToaster(error, t(l.FilterListContainsTheSameFilter));
            }
        } else {
            checkIdExist();
        }
    }, [checkIdExist, data, dynamicSegmentFiltersData, formData, showToaster, t, tabActiveId]);

    const { Component, config } = DynamicSegmentForms[elementType];
    return (
        <Suspense fallback={<BusyLoader isBusy type="spinner" spinnerSize="medium" />}>
            <div className="crm-dynamic-segment-form">
                <div className="crm-dynamic-segment-form-head">{parentDisplayName}</div>
                <Component
                    data={data}
                    config={config}
                    setValidation={setErrorValidationScenario}
                    setError={setErrorData}
                    filterList={dynamicSegmentFiltersData[tabActiveId].filters}
                    validation={errorValidationScenario}
                    error={errorData}
                    setFormData={setFormData}
                    handleApply={handleApply}
                    editModeCancelHandler={editModeCancelHandler}
                />
            </div>
        </Suspense>
    );
};

DynamicSegmentFilterFormWrapper.propTypes = {
    data: PropTypes.instanceOf(UIHashComparisonContainer),
    listLength: PropTypes.number,
    editModeCancelHandler: PropTypes.func,
    setReadOnlyFilterContainer: PropTypes.func,
    tabActiveId: PropTypes.string.isRequired,
};

export default DynamicSegmentFilterFormWrapper;
